import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Gift01,
  Lock01,
  Mail01,
  Minus,
  Plus,
  Radio,
  RadioGroup,
  Skeleton,
  TextInput,
  Textarea,
  TileGroup,
  User01,
} from "@opendate-app/setlist";
import TurnstileChallenge from "../../../shared/TurnstileChallenge";
import TurnstileDisclosure from "../../../shared/TurnstileDisclosure";
import { paymentElementOptions } from "../../../shared/stripePaymentElementOptions";
import { formatCents } from "../../GiftCardProducts/giftCardFormat";
import {
  CODE_AMOUNT_OUT_OF_RANGE,
  CODE_DELIVERY_DATE_INVALID,
  CODE_OVER_PER_ORDER_LIMIT,
  SEND_LATER,
  SEND_NOW,
  parseIsoDate,
} from "../useGiftCardPurchase";
import { deliveryShape, productShape, teamShape } from "../giftCardPurchasePropTypes";

const CUSTOM_AMOUNT = "custom";
const GIFT = "gift";
const FOR_ME = "self";
const MAX_AMOUNT_COLUMNS = 4;

const bonusEndLabel = (endsOn) =>
  parseIsoDate(endsOn).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export const bonusHint = (bonus, currency) =>
  `Get a free ${formatCents(bonus.amountCents, currency, { whole: true })} bonus card with each card of ${formatCents(bonus.minAmountCents, currency, { whole: true })} or more${bonus.endsOn ? `, through ${bonusEndLabel(bonus.endsOn)}` : ""}.`;

const checkedFrom = (value) => (value && value.target ? value.target.checked : Boolean(value));
const firstWord = (name) => name.trim().split(/\s+/)[0];

const errorFor = (purchase, code) => {
  if (purchase.quoteError && purchase.quoteError.code === code) return purchase.quoteError.message;
  if (purchase.error && purchase.error.code === code) return purchase.error.message;
  return null;
};

const FIELD_ERROR_CODES = [CODE_AMOUNT_OUT_OF_RANGE, CODE_OVER_PER_ORDER_LIMIT, CODE_DELIVERY_DATE_INVALID];

const GiftCardPurchaseForm = ({ product, team, currency, messageMaxLength, delivery, turnstileSiteKey, purchase }) => {
  const stripe = useStripe();
  const elements = useElements();
  const turnstileRef = useRef(null);
  const [paymentReady, setPaymentReady] = useState(false);

  const amountItems = product.denominationsCents.map((cents) => ({
    value: cents,
    label: formatCents(cents, currency, { whole: true }),
  }));
  if (product.customMinCents) amountItems.push({ value: CUSTOM_AMOUNT, label: "Other" });

  const quote = purchase.quote;
  const quotedOffer = quote?.bonus_offer;
  const bonus = !quote
    ? product.bonus
    : quotedOffer && {
        amountCents: quotedOffer.amount_cents,
        minAmountCents: quotedOffer.min_amount_cents,
        endsOn: quotedOffer.ends_on,
      };
  const unitLabel = purchase.amountCents ? formatCents(purchase.amountCents, currency) : "Gift card";
  const generalError =
    purchase.error && !FIELD_ERROR_CODES.includes(purchase.error.code)
      ? purchase.error
      : null;
  const quoteProblem =
    purchase.quoteError && ![CODE_AMOUNT_OUT_OF_RANGE, CODE_OVER_PER_ORDER_LIMIT].includes(purchase.quoteError.code)
      ? purchase.quoteError
      : null;

  const recipientFirstName = purchase.recipientName.trim() ? firstWord(purchase.recipientName) : "them";

  const submit = (event) => {
    event.preventDefault();
    purchase.pay({ stripe, elements, turnstileRef });
  };

  return (
    <Card padding="none">
      <form className="gift-card-purchase-form" onSubmit={submit} noValidate>
        <div className="gift-card-purchase-section">
          <span className="gift-card-purchase-label" id="gift-card-amount-label">
            Amount
          </span>
          <TileGroup
            aria-labelledby="gift-card-amount-label"
            name="gift-card-amount"
            items={amountItems}
            columns={Math.min(amountItems.length, MAX_AMOUNT_COLUMNS)}
            value={purchase.isCustomAmount ? CUSTOM_AMOUNT : purchase.selectedAmountCents}
            onChange={purchase.chooseAmount}
          />
          {bonus && (
            <p className="gift-card-purchase-hint" data-testid="gift-card-bonus-hint">
              {bonusHint(bonus, currency)}
            </p>
          )}
          {purchase.isCustomAmount ? (
            <TextInput
              id="gift-card-custom-amount"
              label="Your amount"
              leadingText="$"
              inputMode="decimal"
              value={purchase.customAmount}
              onChange={(event) => purchase.setCustomAmount(event.target.value)}
              hint={`From ${formatCents(product.customMinCents, currency, { whole: true })} to ${formatCents(product.customMaxCents, currency, { whole: true })}`}
              error={errorFor(purchase, CODE_AMOUNT_OUT_OF_RANGE) || purchase.fieldErrors.customAmount}
            />
          ) : (
            errorFor(purchase, CODE_AMOUNT_OUT_OF_RANGE) && (
              <p className="gift-card-purchase-field-error" role="alert">
                {errorFor(purchase, CODE_AMOUNT_OUT_OF_RANGE)}
              </p>
            )
          )}
          <div className="gift-card-purchase-quantity">
            <div>
              <span className="gift-card-purchase-label">Quantity</span>
              <span className="gift-card-purchase-hint">Up to {product.maxPerOrder} per order</span>
            </div>
            <div className="gift-card-purchase-stepper">
              <Button
                variant="secondary"
                size="sm"
                icon={Minus}
                aria-label="One fewer"
                disabled={purchase.quantity <= 1}
                onClick={() => purchase.changeQuantity(purchase.quantity - 1)}
              />
              <span className="gift-card-purchase-quantity-value" aria-live="polite" data-testid="gift-card-quantity">
                {purchase.quantity}
              </span>
              <Button
                variant="secondary"
                size="sm"
                icon={Plus}
                aria-label="One more"
                disabled={purchase.quantity >= product.maxPerOrder}
                onClick={() => purchase.changeQuantity(purchase.quantity + 1)}
              />
            </div>
          </div>
          {errorFor(purchase, CODE_OVER_PER_ORDER_LIMIT) && (
            <p className="gift-card-purchase-field-error" role="alert">
              {errorFor(purchase, CODE_OVER_PER_ORDER_LIMIT)}
            </p>
          )}
        </div>

        <div className="gift-card-purchase-section">
          <TileGroup
            aria-label="Who it's for"
            name="gift-card-recipient"
            columns={2}
            items={[
              { value: GIFT, label: "Send as a gift", icon: Gift01 },
              { value: FOR_ME, label: "For me", icon: User01 },
            ]}
            value={purchase.isGift ? GIFT : FOR_ME}
            onChange={(value) => purchase.setIsGift(value === GIFT)}
          />
          {purchase.isGift ? (
            <>
              <div className="gift-card-purchase-row">
                <TextInput
                  id="gift-card-recipient-name"
                  label="Recipient name"
                  autoComplete="off"
                  value={purchase.recipientName}
                  onChange={(event) => purchase.setRecipientName(event.target.value)}
                  error={purchase.fieldErrors.recipientName}
                />
                <TextInput
                  id="gift-card-recipient-email"
                  label="Recipient email"
                  type="email"
                  autoComplete="off"
                  value={purchase.recipientEmail}
                  onChange={(event) => purchase.setRecipientEmail(event.target.value)}
                  error={purchase.fieldErrors.recipientEmail}
                />
              </div>
              <Textarea
                id="gift-card-message"
                label="Message (optional)"
                rows={3}
                maxLength={messageMaxLength}
                value={purchase.message}
                onChange={(event) => purchase.setMessage(event.target.value)}
                hint={`${purchase.message.length}/${messageMaxLength}`}
              />
              <div className="gift-card-purchase-delivery">
                <span className="gift-card-purchase-label" id="gift-card-send-when-label">
                  When should we send it?
                </span>
                <RadioGroup
                  aria-labelledby="gift-card-send-when-label"
                  name="gift-card-send-when"
                  value={purchase.sendWhen}
                  onChange={purchase.setSendWhen}
                >
                  <Radio value={SEND_NOW} label="Send now" />
                  <Radio value={SEND_LATER} label="Pick a date" />
                </RadioGroup>
                {purchase.sendWhen === SEND_LATER && (
                  <DatePicker
                    id="gift-card-deliver-on"
                    label="Delivery date"
                    value={purchase.deliverOn}
                    onChange={purchase.setDeliverOn}
                    min={parseIsoDate(delivery.earliestDate)}
                    max={parseIsoDate(delivery.latestDate)}
                    hint={`We send it at ${delivery.timeLabel}.`}
                    error={purchase.fieldErrors.deliverOn || errorFor(purchase, CODE_DELIVERY_DATE_INVALID)}
                  />
                )}
              </div>
              <p className="gift-card-purchase-note">
                <Mail01 aria-hidden="true" />
                {purchase.sendWhen === SEND_LATER
                  ? `We’ll email it to ${recipientFirstName} on the day you pick.`
                  : `We’ll email it to ${recipientFirstName} after you pay.`}
              </p>
            </>
          ) : (
            <p className="gift-card-purchase-note">
              <Mail01 aria-hidden="true" />
              We&rsquo;ll email the gift card to you after you pay.
            </p>
          )}
        </div>

        <div className="gift-card-purchase-section">
          <span className="gift-card-purchase-label">Your details</span>
          <div className="gift-card-purchase-row">
            <TextInput
              id="gift-card-first-name"
              label="First name"
              autoComplete="given-name"
              value={purchase.firstName}
              onChange={(event) => purchase.setFirstName(event.target.value)}
              error={purchase.fieldErrors.firstName}
            />
            <TextInput
              id="gift-card-last-name"
              label="Last name"
              autoComplete="family-name"
              value={purchase.lastName}
              onChange={(event) => purchase.setLastName(event.target.value)}
              error={purchase.fieldErrors.lastName}
            />
          </div>
          <TextInput
            id="gift-card-email"
            label="Email"
            type="email"
            autoComplete="email"
            value={purchase.email}
            onChange={(event) => purchase.setEmail(event.target.value)}
            hint="Your receipt goes here."
            error={purchase.fieldErrors.email}
          />
        </div>

        <div className="gift-card-purchase-section">
          <span className="gift-card-purchase-label">Payment</span>
          {!paymentReady && <Skeleton shape="block" height={96} label="Loading payment form" />}
          <div className={paymentReady ? undefined : "gift-card-purchase-hidden"}>
            <PaymentElement options={paymentElementOptions()} onReady={() => setPaymentReady(true)} />
          </div>
        </div>

        <div className="gift-card-purchase-section gift-card-purchase-summary" aria-live="polite">
          {quote ? (
            <>
              <div className="gift-card-purchase-summary-line">
                <span>
                  {unitLabel} gift card &times; {purchase.quantity}
                </span>
                <span>{formatCents(quote.subtotal_cents, currency)}</span>
              </div>
              {quote.fees_cents > 0 && (
                <div className="gift-card-purchase-summary-line">
                  <span>Fees</span>
                  <span>{formatCents(quote.fees_cents, currency)}</span>
                </div>
              )}
              <div className="gift-card-purchase-summary-line gift-card-purchase-summary-total">
                <span>Total</span>
                <span data-testid="gift-card-total">{formatCents(quote.total_cents, currency)}</span>
              </div>
            </>
          ) : (
            purchase.quoteLoading && <Skeleton shape="text" lines={3} label="Updating the total" />
          )}
          {quoteProblem && <Alert color="error" title={quoteProblem.message} />}
          <Checkbox
            id="gift-card-agree"
            checked={purchase.agreed}
            onChange={(value) => purchase.setAgreed(checkedFrom(value))}
            label={
              <span>
                I agree to {team.name}&rsquo;s gift card terms and Opendate&rsquo;s{" "}
                <a href="https://www.opendate.io/terms" target="_blank" rel="noopener noreferrer">
                  terms of service
                </a>
                .
              </span>
            }
          />
          {purchase.fieldErrors.agreed && (
            <p className="gift-card-purchase-field-error" role="alert">
              {purchase.fieldErrors.agreed}
            </p>
          )}
          {generalError && <Alert color="error" role="alert" title={generalError.message} />}
          <Button
            type="submit"
            variant="primary"
            icon={Lock01}
            disabled={purchase.submitting || !stripe || !quote}
            className="gift-card-purchase-pay"
          >
            {purchase.submitting ? "Processing…" : `Pay ${quote ? formatCents(quote.total_cents, currency) : ""}`.trim()}
          </Button>
          <TurnstileChallenge ref={turnstileRef} sitekey={turnstileSiteKey} />
          <TurnstileDisclosure />
        </div>
      </form>
    </Card>
  );
};

GiftCardPurchaseForm.propTypes = {
  product: productShape.isRequired,
  team: teamShape.isRequired,
  currency: PropTypes.string.isRequired,
  messageMaxLength: PropTypes.number.isRequired,
  delivery: deliveryShape.isRequired,
  turnstileSiteKey: PropTypes.string,
  purchase: PropTypes.object.isRequired,
};

export default GiftCardPurchaseForm;
